import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useEmail } from '../../contexts/EmailContext';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';
import { FiCalendar, FiDownload, FiEye, FiMail, FiMousePointer, FiRefreshCw, FiTrendingUp, FiUsers } from 'react-icons/fi';


const EmailAnalyticsDashboard = () => {
  const { getEmailAnalytics } = useEmail();
  const [dateRange, setDateRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('all');
  
  const analytics = getEmailAnalytics(dateRange);

  const metricCards = [
    {
      title: 'Total Campaigns',
      value: analytics.totalCampaigns,
      change: '+12%',
      trend: 'up',
      icon: FiMail,
      color: 'blue'
    },
    {
      title: 'Emails Sent',
      value: analytics.emailsSent.toLocaleString(),
      change: '+8%',
      trend: 'up',
      icon: FiUsers,
      color: 'green'
    },
    {
      title: 'Average Open Rate',
      value: `${analytics.avgOpenRate}%`,
      change: '+2.1%',
      trend: 'up',
      icon: FiEye,
      color: 'purple'
    },
    {
      title: 'Average Click Rate',
      value: `${analytics.avgClickRate}%`,
      change: '+0.5%',
      trend: 'up',
      icon: FiMousePointer,
      color: 'orange'
    }
  ];

  const getPerformanceChartOption = () => {
    const dates = analytics.dailyStats.map(stat => stat.date);
    const sentData = analytics.dailyStats.map(stat => stat.sent);
    const openedData = analytics.dailyStats.map(stat => stat.opened);
    const clickedData = analytics.dailyStats.map(stat => stat.clicked);

    return {
      title: {
        text: 'Email Performance Trends',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        }
      },
      legend: {
        data: ['Sent', 'Opened', 'Clicked'],
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          formatter: function(value) {
            return new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            });
          }
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Sent',
          type: 'line',
          data: sentData,
          smooth: true,
          lineStyle: {
            color: '#3B82F6'
          },
          itemStyle: {
            color: '#3B82F6'
          }
        },
        {
          name: 'Opened',
          type: 'line',
          data: openedData,
          smooth: true,
          lineStyle: {
            color: '#10B981'
          },
          itemStyle: {
            color: '#10B981'
          }
        },
        {
          name: 'Clicked',
          type: 'line',
          data: clickedData,
          smooth: true,
          lineStyle: {
            color: '#8B5CF6'
          },
          itemStyle: {
            color: '#8B5CF6'
          }
        }
      ]
    };
  };

  const getCampaignComparisonOption = () => {
    return {
      title: {
        text: 'Campaign Performance Comparison',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['Open Rate', 'Click Rate', 'Response Rate'],
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: analytics.campaignComparison.map(c => c.name),
        axisLabel: {
          rotate: 45,
          interval: 0
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}%'
        }
      },
      series: [
        {
          name: 'Open Rate',
          type: 'bar',
          data: analytics.campaignComparison.map(c => c.openRate),
          itemStyle: {
            color: '#3B82F6'
          }
        },
        {
          name: 'Click Rate',
          type: 'bar',
          data: analytics.campaignComparison.map(c => c.clickRate),
          itemStyle: {
            color: '#10B981'
          }
        },
        {
          name: 'Response Rate',
          type: 'bar',
          data: analytics.campaignComparison.map(c => c.responseRate),
          itemStyle: {
            color: '#8B5CF6'
          }
        }
      ]
    };
  };

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Analytics</h2>
          <p className="text-gray-600">
            Track your email campaign performance and engagement metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCalendar} className="text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={FiDownload} className="text-sm" />
            Export
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-1">
                  <SafeIcon 
                    icon={FiTrendingUp} 
                    className={`text-sm mr-1 ${metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}`} 
                  />
                  <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.change}
                  </span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${colorClasses[metric.color]} flex items-center justify-center`}>
                <SafeIcon icon={metric.icon} className="text-white text-xl" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <ReactECharts
            option={getPerformanceChartOption()}
            style={{ height: '400px' }}
            opts={{ renderer: 'canvas' }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <ReactECharts
            option={getCampaignComparisonOption()}
            style={{ height: '400px' }}
            opts={{ renderer: 'canvas' }}
          />
        </motion.div>
      </div>

      {/* Top Performing Campaigns */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Campaigns</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Open Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Click Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ROI
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.topCampaigns.map((campaign, index) => (
                <tr key={campaign.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.category}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.sent.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">{campaign.openRate}%</div>
                      <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(campaign.openRate, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">{campaign.clickRate}%</div>
                      <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(campaign.clickRate * 10, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">{campaign.responseRate}%</div>
                      <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(campaign.responseRate * 20, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      campaign.roi > 200 ? 'bg-green-100 text-green-800' :
                      campaign.roi > 100 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {campaign.roi}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* A/B Test Results */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">A/B Test Results</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {analytics.abTests.map((test, index) => (
            <div key={test.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{test.campaign}</h4>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  test.winner === 'A' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                }`}>
                  Winner: {test.winner}
                </span>
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Subject A:</span>
                    <span className="font-medium">{test.openRateA}%</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{test.subjectA}</p>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Subject B:</span>
                    <span className="font-medium">{test.openRateB}%</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{test.subjectB}</p>
                </div>
                
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span>Improvement:</span>
                    <span className="font-medium text-green-600">+{test.improvement}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default EmailAnalyticsDashboard;